// Client-side carousel renderer: composes 1080×1350 PNG slides on a canvas,
// returns Blobs. No server work — runs entirely in the user's browser.
//
// Layout choices (matching the user's "MVP / 4c" decision):
//   - Cover  : background AI image + headline grosso + brand badge
//   - Body   : gradient brand colors (text-first, no AI image) + numbered
//              headline + paragraph body
//   - Outro  : background AI image + CTA + brand watermark

import JSZip from "jszip";

export const SLIDE_W = 1080;
export const SLIDE_H = 1350; // 4:5 Instagram carousel standard

// ---------- helpers ----------

function hexToRgb(hex, fallback = [255, 68, 0]) {
  try {
    let h = (hex || "").replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  } catch {
    return fallback;
  }
}

function rgbCss(rgb, alpha = 1) {
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("img load failed"));
    img.src = src;
  });
}

function drawCover(ctx, img, dw, dh) {
  const scale = Math.max(dw / img.width, dh / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, (dw - w) / 2, (dh - h) / 2, w, h);
}

function wrapWords(ctx, text, maxWidth) {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  for (const w of words) {
    const trial = cur ? cur + " " + w : w;
    if (ctx.measureText(trial).width <= maxWidth) cur = trial;
    else {
      if (cur) lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

function fitText(ctx, weight, family, text, maxWidth, maxHeight, startSize, minSize, lineGap = 1.15) {
  let size = startSize;
  while (size >= minSize) {
    ctx.font = `${weight} ${size}px ${family}`;
    const lines = wrapWords(ctx, text, maxWidth);
    const widest = Math.max(0, ...lines.map((ln) => ctx.measureText(ln).width));
    const lineH = Math.round(size * lineGap);
    const totalH = lineH * lines.length;
    if (widest <= maxWidth && totalH <= maxHeight) {
      return { size, lines, lineH, totalH };
    }
    size -= 2;
  }
  ctx.font = `${weight} ${minSize}px ${family}`;
  const lines = wrapWords(ctx, text, maxWidth);
  const lineH = Math.round(minSize * lineGap);
  return { size: minSize, lines, lineH, totalH: lineH * lines.length };
}

const FAMILY = '"Helvetica Neue", Arial, sans-serif';

// ---------- slide composers ----------

async function drawCoverSlide(ctx, slide, carousel) {
  const brandRgb = hexToRgb(carousel.brand_color_primary || "#FF4400");

  // background image or gradient fallback
  if (slide.image_base64) {
    try {
      const dataUrl = slide.image_base64.startsWith("data:")
        ? slide.image_base64
        : `data:image/jpeg;base64,${slide.image_base64}`;
      const img = await loadImage(dataUrl);
      drawCover(ctx, img, SLIDE_W, SLIDE_H);
    } catch {
      drawGradientBg(ctx, brandRgb);
    }
  } else {
    drawGradientBg(ctx, brandRgb);
  }

  // dark overlay so text pops
  const overlay = ctx.createLinearGradient(0, SLIDE_H * 0.3, 0, SLIDE_H);
  overlay.addColorStop(0, "rgba(0,0,0,0)");
  overlay.addColorStop(1, "rgba(0,0,0,0.78)");
  ctx.fillStyle = overlay;
  ctx.fillRect(0, 0, SLIDE_W, SLIDE_H);

  // brand badge top
  drawBrandBadge(ctx, carousel, brandRgb, 60, 60);

  // headline near bottom
  const headline = (slide.headline || "").toUpperCase();
  const fit = fitText(ctx, "900", FAMILY, headline, SLIDE_W - 160, 600, 130, 60, 1.06);
  ctx.font = `900 ${fit.size}px ${FAMILY}`;
  let y = SLIDE_H - 200 - fit.totalH;
  if (y < 400) y = 400;
  for (const ln of fit.lines) {
    const w = ctx.measureText(ln).width;
    const x = (SLIDE_W - w) / 2;
    ctx.fillStyle = "rgba(0,0,0,0.85)";
    ctx.fillText(ln, x + 4, y + fit.size + 4);
    ctx.fillStyle = "#fff";
    ctx.fillText(ln, x, y + fit.size);
    y += fit.lineH;
  }

  // body line under headline (small tagline)
  if (slide.body) {
    const bodyFit = fitText(ctx, "600", FAMILY, slide.body, SLIDE_W - 200, 100, 36, 22, 1.3);
    ctx.font = `600 ${bodyFit.size}px ${FAMILY}`;
    let by = y + 30;
    for (const ln of bodyFit.lines) {
      const w = ctx.measureText(ln).width;
      const x = (SLIDE_W - w) / 2;
      ctx.fillStyle = "rgba(0,0,0,0.85)";
      ctx.fillText(ln, x + 1, by + bodyFit.size + 1);
      ctx.fillStyle = "#e5e5e5";
      ctx.fillText(ln, x, by + bodyFit.size);
      by += bodyFit.lineH;
    }
  }

  drawSwipeHint(ctx, brandRgb);
}

async function drawBodySlide(ctx, slide, carousel) {
  const brandRgb = hexToRgb(carousel.brand_color_primary || "#FF4400");
  const secondaryRgb = hexToRgb(carousel.brand_color_secondary || "#FFB800");

  // Background priority (per user request — "intelligent background selector"):
  //   - body_style explicitly set to solid_dark / blur_cover / ai_photo →
  //     ALWAYS wins, even if this slide has its own image. The user picked
  //     a global look and expects it to apply everywhere.
  //   - body_style == "gradient" (default) → if the slide has its own image,
  //     keep the legacy "image+text" mode; otherwise draw the brand gradient.
  const style = carousel.body_style || "gradient";
  let hasBgImage = false;

  const tryDrawSlideImage = async () => {
    if (!slide.image_base64) return false;
    try {
      const dataUrl = slide.image_base64.startsWith("data:")
        ? slide.image_base64
        : `data:image/jpeg;base64,${slide.image_base64}`;
      const img = await loadImage(dataUrl);
      drawCover(ctx, img, SLIDE_W, SLIDE_H);
      ctx.fillStyle = "rgba(0,0,0,0.62)";
      ctx.fillRect(0, 0, SLIDE_W, SLIDE_H);
      return true;
    } catch {
      return false;
    }
  };

  if (style === "solid_dark") {
    drawSolidDarkBg(ctx, brandRgb);
  } else if (style === "blur_cover") {
    const coverSlide = (carousel.slides || []).find((s) => s.role === "cover");
    const coverB64 = coverSlide?.image_base64;
    let drawn = false;
    if (coverB64) {
      try {
        const dataUrl = coverB64.startsWith("data:")
          ? coverB64
          : `data:image/jpeg;base64,${coverB64}`;
        const img = await loadImage(dataUrl);
        await drawBlurredCover(ctx, img);
        ctx.fillStyle = "rgba(0,0,0,0.55)";
        ctx.fillRect(0, 0, SLIDE_W, SLIDE_H);
        hasBgImage = true;
        drawn = true;
      } catch {}
    }
    if (!drawn) drawGradientBg(ctx, brandRgb, secondaryRgb);
  } else if (style === "ai_photo" && carousel.body_bg_image) {
    try {
      const b64 = carousel.body_bg_image;
      const dataUrl = b64.startsWith("data:") ? b64 : `data:image/jpeg;base64,${b64}`;
      const img = await loadImage(dataUrl);
      drawCover(ctx, img, SLIDE_W, SLIDE_H);
      ctx.fillStyle = "rgba(0,0,0,0.58)";
      ctx.fillRect(0, 0, SLIDE_W, SLIDE_H);
      hasBgImage = true;
    } catch {
      drawGradientBg(ctx, brandRgb, secondaryRgb);
    }
  } else if (style === "gradient") {
    // EXPLICIT "gradient": the user picked the gradient swatch — they
    // expect to see the brand colors, NOT the per-slide photo. The old
    // behaviour silently fell back to slide.image_base64 when present,
    // which is exactly the bug the user reported ("clicco Gradiente ma
    // resta la foto"). The per-slide photo is still reachable via the
    // bg-picker buttons (Foto Galleria / Camera / Rigenera).
    drawGradientBg(ctx, brandRgb, secondaryRgb);
  } else {
    // Unknown / legacy value (no body_style set yet) — keep the old
    // behaviour of using the per-slide image if it exists, otherwise the
    // brand gradient. This branch is only hit for documents that never
    // touched the bg-style picker.
    const used = await tryDrawSlideImage();
    if (used) {
      hasBgImage = true;
    } else {
      drawGradientBg(ctx, brandRgb, secondaryRgb);
    }
  }

  // brand badge top
  drawBrandBadge(ctx, carousel, [255, 255, 255], 60, 60, true /* light */);

  // huge index number on the right side as a graphic accent.
  // Keep it subtle (10% alpha) — a touch louder when no bg image, so it
  // contributes more to the gradient-only layout.
  const idx = slide.slide_number;
  ctx.font = `900 360px ${FAMILY}`;
  ctx.fillStyle = hasBgImage ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.10)";
  const idxText = String(idx);
  const idxW = ctx.measureText(idxText).width;
  ctx.fillText(idxText, SLIDE_W - idxW - 60, 360);

  // headline
  const headline = (slide.headline || "").toUpperCase();
  const headlineFit = fitText(ctx, "900", FAMILY, headline, SLIDE_W - 160, 360, 92, 40, 1.06);
  ctx.font = `900 ${headlineFit.size}px ${FAMILY}`;
  ctx.fillStyle = "#ffffff";
  let hy = 280;
  for (const ln of headlineFit.lines) {
    if (hasBgImage) {
      ctx.fillStyle = "rgba(0,0,0,0.85)";
      ctx.fillText(ln, 80 + 3, hy + headlineFit.size + 3);
      ctx.fillStyle = "#ffffff";
    }
    ctx.fillText(ln, 80, hy + headlineFit.size);
    hy += headlineFit.lineH;
  }

  // body paragraph below the headline
  const bodyText = slide.body || "";
  const bodyAvailH = SLIDE_H - hy - 220; // leave room for swipe hint
  const bodyFit = fitText(ctx, "500", FAMILY, bodyText, SLIDE_W - 160, bodyAvailH, 50, 26, 1.42);
  ctx.font = `500 ${bodyFit.size}px ${FAMILY}`;
  let by = hy + 60;
  for (const ln of bodyFit.lines) {
    if (hasBgImage) {
      ctx.fillStyle = "rgba(0,0,0,0.85)";
      ctx.fillText(ln, 80 + 2, by + bodyFit.size + 2);
    }
    ctx.fillStyle = "rgba(255,255,255,0.95)";
    ctx.fillText(ln, 80, by + bodyFit.size);
    by += bodyFit.lineH;
  }

  drawSwipeHint(ctx, [255, 255, 255]);
}

async function drawOutroSlide(ctx, slide, carousel) {
  const brandRgb = hexToRgb(carousel.brand_color_primary || "#FF4400");

  // background image or gradient fallback
  if (slide.image_base64) {
    try {
      const dataUrl = slide.image_base64.startsWith("data:")
        ? slide.image_base64
        : `data:image/jpeg;base64,${slide.image_base64}`;
      const img = await loadImage(dataUrl);
      drawCover(ctx, img, SLIDE_W, SLIDE_H);
    } catch {
      drawGradientBg(ctx, brandRgb);
    }
  } else {
    drawGradientBg(ctx, brandRgb);
  }

  // strong dark overlay
  ctx.fillStyle = "rgba(0,0,0,0.65)";
  ctx.fillRect(0, 0, SLIDE_W, SLIDE_H);

  // brand badge top
  drawBrandBadge(ctx, carousel, brandRgb, 60, 60);

  // big CTA centered
  const headline = (slide.headline || "").toUpperCase();
  const fit = fitText(ctx, "900", FAMILY, headline, SLIDE_W - 160, 500, 120, 50, 1.08);
  ctx.font = `900 ${fit.size}px ${FAMILY}`;
  let y = (SLIDE_H - fit.totalH) / 2 - 100;
  for (const ln of fit.lines) {
    const w = ctx.measureText(ln).width;
    const x = (SLIDE_W - w) / 2;
    ctx.fillStyle = "rgba(0,0,0,0.85)";
    ctx.fillText(ln, x + 4, y + fit.size + 4);
    ctx.fillStyle = "#fff";
    ctx.fillText(ln, x, y + fit.size);
    y += fit.lineH;
  }

  // CTA pill below
  if (slide.body) {
    const ctaText = slide.body;
    const fitCta = fitText(ctx, "700", FAMILY, ctaText, SLIDE_W - 240, 200, 40, 22, 1.3);
    ctx.font = `700 ${fitCta.size}px ${FAMILY}`;
    const innerPadX = 60;
    const widest = Math.max(...fitCta.lines.map((l) => ctx.measureText(l).width));
    const rectW = Math.min(widest + 2 * innerPadX, SLIDE_W - 200);
    const lineH = fitCta.lineH;
    const rectH = lineH * fitCta.lines.length + 50;
    const rectX = (SLIDE_W - rectW) / 2;
    const rectY = y + 50;

    // rounded pill
    ctx.fillStyle = rgbCss(brandRgb);
    roundedRect(ctx, rectX, rectY, rectW, rectH, 18);
    ctx.fill();

    ctx.fillStyle = "#fff";
    let ty = rectY + 25 + fitCta.size;
    for (let i = 0; i < fitCta.lines.length; i++) {
      const ln = fitCta.lines[i];
      const w = ctx.measureText(ln).width;
      const x = (SLIDE_W - w) / 2;
      ctx.fillText(ln, x, ty);
      ty += lineH;
    }
  }
}

// ---------- custom layout ----------
//
// When the carousel is in "custom" mode the user has explicitly picked
// colors, a card shape and a text position. We trust those choices and
// keep the background image at its natural exposure (no dark gradient).
//
// Layout chain: slide.layout > carousel.layout > hard defaults

const DEFAULT_LAYOUT = {
  text_color: "#ffffff",
  card_bg_color: "#000000",
  card_bg_alpha: 0.50,
  card_shape: "frosted",   // rect | rounded | frosted
  text_x: 0.20,            // 0..1, card center on the slide width — defaults to LEFT
  text_y: 0.50,            // 0..1, card center on the slide height — vertically centered
  card_width: 0.35,        // 0.2..1.0, card width as fraction of slide width — narrow column on the left
  card_height: 0.22,       // 0.08..0.95, card height as fraction of slide height. Always defined so
                           // the on-screen drag handle (corner pins) and the canvas-rendered card
                           // share identical geometry — no more drifting outlines.
  text_split: null,        // 0.15..0.85 headline-share when set; null = use length-based heuristic.
                           // MUST be listed here so resolveLayout() actually copies it through
                           // from carousel.layout / slide.layout — the iterator below only walks
                           // DEFAULT_LAYOUT keys.
};

function resolveLayout(slide, carousel) {
  // Merge in priority order, skipping undefined keys so partial overrides
  // (e.g. only text_y) inherit the rest from the carousel default.
  const out = { ...DEFAULT_LAYOUT };
  const cl = carousel?.layout || {};
  const sl = slide?.layout || {};
  for (const k of Object.keys(DEFAULT_LAYOUT)) {
    if (cl[k] !== undefined && cl[k] !== null) out[k] = cl[k];
    if (sl[k] !== undefined && sl[k] !== null) out[k] = sl[k];
  }
  return out;
}

async function drawCustomSlide(ctx, slide, carousel) {
  const brandRgb = hexToRgb(carousel.brand_color_primary || "#FF4400");
  const secondaryRgb = hexToRgb(carousel.brand_color_secondary || "#FFB800");
  const layout = resolveLayout(slide, carousel);

  // ---- background ----
  // In custom layout mode the user's intent is explicit: "use the slide's
  // own photo (from the reel) as the background, drag the text card on
  // top". So we ALWAYS prioritize slide.image_base64 here regardless of
  // body_style — body_style only applies in auto mode (the "proposed
  // backgrounds" macro option). Fallback chain: per-slide image →
  // body_bg_image → cover image → brand gradient.
  let drewBg = false;
  const sources = [];
  if (slide.image_base64) sources.push(slide.image_base64);
  if (carousel.body_bg_image) sources.push(carousel.body_bg_image);
  const coverFallback = (carousel.slides || []).find((s) => s.role === "cover");
  if (coverFallback && coverFallback.image_base64) sources.push(coverFallback.image_base64);
  for (const src of sources) {
    if (drewBg) break;
    try {
      const dataUrl = src.startsWith("data:") ? src : `data:image/jpeg;base64,${src}`;
      const img = await loadImage(dataUrl);
      drawCover(ctx, img, SLIDE_W, SLIDE_H);
      drewBg = true;
    } catch { /* try next */ }
  }
  if (!drewBg) {
    drawGradientBg(ctx, brandRgb, secondaryRgb);
  }

  // ---- text card ----
  // The card always has an explicit height (DEFAULT_LAYOUT.card_height
  // guarantees a value) so the on-screen overlay handles match the canvas
  // exactly. Text is auto-scaled with proportional starting sizes — a
  // bigger box → bigger fonts; fitText shrinks to fit when needed.
  const cardW = Math.round(SLIDE_W * layout.card_width);
  const cardH = Math.round(SLIDE_H * layout.card_height);
  const padX = 60;
  const padY = 50;
  const innerW = Math.max(40, cardW - 2 * padX);
  const innerH = Math.max(40, cardH - 2 * padY);

  const headlineText = (slide.headline || "").toUpperCase();
  const bodyText = slide.body || "";
  const gap = headlineText && bodyText ? 30 : 0;

  // Vertical budget split:
  //  - If layout.text_split is set explicitly, the user has dragged the
  //    in-card divider and we honor that value verbatim.
  //  - Otherwise, fall back to a heuristic based on body length so long
  //    copy still gets enough room without manual tweaking.
  const bodyLen = bodyText.length;
  const heuristicShare = bodyText
    ? bodyLen >= 200 ? 0.62
      : bodyLen >= 120 ? 0.55
      : bodyLen >= 60  ? 0.45
      : 0.35
    : 0;
  const explicitSplit =
    typeof layout.text_split === "number" && layout.text_split >= 0.05 && layout.text_split <= 0.95
      ? layout.text_split
      : null;
  // text_split = headline share (top portion). bodyShare = 1 - text_split.
  const bodyShare = explicitSplit !== null ? (1 - explicitSplit) : heuristicShare;
  const headlineBudget = bodyText ? Math.max(36, innerH * (1 - bodyShare) - gap) : innerH;
  const bodyBudget = bodyText
    ? Math.max(24, innerH - headlineBudget - gap)
    : 0;
  // Starting font sizes scale with the available area. The body starts
  // at ~90% of its budget so it lands close to the headline. Caps are
  // high enough that very tall boxes can host genuinely large fonts; for
  // smaller boxes fitText decrements until both width and height fit.
  const hStart = Math.max(28, Math.min(500, Math.round(headlineBudget)));
  const bStart = Math.max(20, Math.min(420, Math.round(bodyBudget * 0.9)));
  let headlineFit = headlineText
    ? fitText(ctx, "900", FAMILY, headlineText, innerW, headlineBudget, hStart, 20, 0.82)
    : null;
  const bodyFit = bodyText
    ? fitText(ctx, "500", FAMILY, bodyText, innerW, bodyBudget, bStart, 18, 1.35)
    : null;

  // Cap the visual ratio between headline and body — UNLESS the user has
  // explicitly pinned the divider position via `text_split`, in which case
  // we trust their choice. Without this cap (and without an explicit
  // split), a short headline can grow to its width-bound maximum (huge)
  // while a long body shrinks down to fit its budget — producing the
  // "10× bigger" look. We rebuild the headline with a smaller cap so the
  // ratio stays below MAX_RATIO; the freed vertical slack feeds back into
  // stretchFit which expands the line spacing.
  const MAX_RATIO = 2.2;
  if (
    explicitSplit === null &&
    headlineFit && bodyFit &&
    headlineFit.size > bodyFit.size * MAX_RATIO
  ) {
    const cappedStart = Math.round(bodyFit.size * MAX_RATIO);
    headlineFit = fitText(
      ctx, "900", FAMILY, headlineText,
      innerW, headlineBudget, cappedStart, 20, 0.82,
    );
  }

  // When the headline (or body) is width-bound — i.e., its longest word
  // already maxes out the card width at the current font size — `fitText`
  // can't grow it further, so a taller box leaves vertical empty space
  // sitting around the text. Redistribute that slack across the line
  // gaps so the content visually fills the box. Cap the expansion at 2.2×
  // the original font size to avoid grotesque "double spaced" looks on
  // very tall boxes.
  const stretchFit = (fit, budget) => {
    if (!fit) return fit;
    if (fit.lines.length < 1) return fit;
    if (fit.totalH >= budget * 0.92) return fit;
    const maxLineH = Math.round(fit.size * 2.2);
    const idealLineH = Math.floor(budget / fit.lines.length);
    const newLineH = Math.min(idealLineH, maxLineH);
    if (newLineH <= fit.lineH) return fit;
    return { ...fit, lineH: newLineH, totalH: newLineH * fit.lines.length };
  };
  const headlineDraw = stretchFit(headlineFit, headlineBudget);
  const bodyDraw = stretchFit(bodyFit, bodyBudget);
  // Also stretch the gap between headline and body proportionally to any
  // remaining slack — keeps the two blocks balanced inside the bigger card.
  const baseGap = gap;
  const usedH =
    (headlineDraw ? headlineDraw.totalH : 0) +
    (bodyDraw ? bodyDraw.totalH : 0) +
    baseGap;
  const extraSlack = Math.max(0, innerH - usedH);
  const finalGap =
    headlineDraw && bodyDraw
      ? Math.min(baseGap + Math.floor(extraSlack * 0.4), Math.round(headlineDraw.size * 1.5))
      : baseGap;

  // Position card center on (text_x, text_y) but clamp to viewport
  const cx = SLIDE_W * layout.text_x;
  const cy = SLIDE_H * layout.text_y;
  let cardX = Math.round(cx - cardW / 2);
  let cardY = Math.round(cy - cardH / 2);
  cardX = Math.max(20, Math.min(SLIDE_W - cardW - 20, cardX));
  cardY = Math.max(20, Math.min(SLIDE_H - cardH - 20, cardY));

  const bgRgb = hexToRgb(layout.card_bg_color, [0, 0, 0]);
  const bgAlpha = Math.max(0, Math.min(1, layout.card_bg_alpha));

  // Render the card chrome based on the user's chosen shape.
  ctx.save();
  if (layout.card_shape === "frosted") {
    // Simulate a frosted-glass effect: lift a blurred snapshot of the
    // background ONLY inside the card rectangle, then drop a semi-opaque
    // tint on top. Works on every modern browser (canvas.filter is widely
    // supported); a hard-color fallback would just look like "rect" mode.
    const supportsFilter = "filter" in ctx;
    if (supportsFilter) {
      // We need a separate canvas for the blurred slice — clipping the
      // ctx with a path + applying `filter` re-blurs the entire content,
      // so do it offscreen.
      const off = document.createElement("canvas");
      off.width = SLIDE_W;
      off.height = SLIDE_H;
      const octx = off.getContext("2d");
      // Reuse already-drawn ctx as the source
      octx.drawImage(ctx.canvas, 0, 0);
      ctx.save();
      roundedRect(ctx, cardX, cardY, cardW, cardH, 32);
      ctx.clip();
      ctx.filter = "blur(28px) saturate(140%) brightness(0.95)";
      ctx.drawImage(off, 0, 0);
      ctx.filter = "none";
      ctx.restore();
    }
    ctx.fillStyle = rgbCss(bgRgb, bgAlpha);
    roundedRect(ctx, cardX, cardY, cardW, cardH, 32);
    ctx.fill();
    // Thin highlight border
    ctx.strokeStyle = "rgba(255,255,255,0.22)";
    ctx.lineWidth = 2;
    ctx.stroke();
  } else {
    ctx.fillStyle = rgbCss(bgRgb, bgAlpha);
    const radius = layout.card_shape === "rounded" ? 32 : 0;
    if (radius > 0) {
      roundedRect(ctx, cardX, cardY, cardW, cardH, radius);
      ctx.fill();
    } else {
      ctx.fillRect(cardX, cardY, cardW, cardH);
    }
  }
  ctx.restore();

  // ---- draw the text content inside the card ----
  ctx.textBaseline = "alphabetic";
  const textColor = layout.text_color || "#fff";
  // Vertically center the (possibly stretched) content inside the card.
  const contentH =
    (headlineDraw ? headlineDraw.totalH : 0) +
    (bodyDraw ? bodyDraw.totalH : 0) +
    (headlineDraw && bodyDraw ? finalGap : 0);
  let ty = cardY + Math.max(padY, (cardH - contentH) / 2);
  if (headlineDraw) {
    ctx.font = `900 ${headlineDraw.size}px ${FAMILY}`;
    ctx.fillStyle = textColor;
    // Use the (possibly stretched) lineH; align glyph baseline to the
    // bottom of each "row" so the visual spacing matches lineH.
    const baselineOffset = Math.round((headlineDraw.lineH + headlineDraw.size) / 2);
    for (const ln of headlineDraw.lines) {
      const w = ctx.measureText(ln).width;
      const x = cardX + (cardW - w) / 2;
      ctx.fillText(ln, x, ty + baselineOffset);
      ty += headlineDraw.lineH;
    }
  }
  if (bodyDraw) {
    if (headlineDraw) ty += finalGap;
    ctx.font = `500 ${bodyDraw.size}px ${FAMILY}`;
    ctx.fillStyle = textColor;
    const baselineOffset = Math.round((bodyDraw.lineH + bodyDraw.size) / 2);
    for (const ln of bodyDraw.lines) {
      const w = ctx.measureText(ln).width;
      const x = cardX + (cardW - w) / 2;
      ctx.fillText(ln, x, ty + baselineOffset);
      ty += bodyDraw.lineH;
    }
  }

  // Brand badge stays in the corner so users can identify the source.
  // Render it light so it works against the natural photo.
  drawBrandBadge(ctx, carousel, [255, 255, 255], 60, 60, true);
}

// ---------- shared decorations ----------

function drawGradientBg(ctx, primary, secondary) {
  // Diagonal brand gradient. If only primary is given, blend toward darker.
  const g = ctx.createLinearGradient(0, 0, SLIDE_W, SLIDE_H);
  g.addColorStop(0, rgbCss(primary, 1));
  if (secondary) {
    g.addColorStop(1, rgbCss(secondary, 1));
  } else {
    const dark = primary.map((c) => Math.max(0, Math.floor(c * 0.45)));
    g.addColorStop(1, rgbCss(dark, 1));
  }
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, SLIDE_W, SLIDE_H);
}

// Near-black background with a subtle brand-colored radial glow in the corner.
// Reads cleanly against white text and matches the dark Obsidian app theme.
function drawSolidDarkBg(ctx, primary) {
  ctx.fillStyle = "#0a0a0c";
  ctx.fillRect(0, 0, SLIDE_W, SLIDE_H);
  const radius = SLIDE_W * 0.9;
  const g = ctx.createRadialGradient(
    SLIDE_W * 0.85, SLIDE_H * 0.15, 40,
    SLIDE_W * 0.85, SLIDE_H * 0.15, radius,
  );
  g.addColorStop(0, rgbCss(primary, 0.35));
  g.addColorStop(1, rgbCss(primary, 0));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, SLIDE_W, SLIDE_H);
}

// Draw the cover slide image with a heavy blur. We use canvas filter when
// available and fall back to a downscale-upscale trick on older engines.
async function drawBlurredCover(ctx, img) {
  const supportsFilter = "filter" in ctx;
  if (supportsFilter) {
    ctx.save();
    ctx.filter = "blur(40px) saturate(120%)";
    drawCover(ctx, img, SLIDE_W, SLIDE_H);
    ctx.filter = "none";
    ctx.restore();
    return;
  }
  // Fallback: scale image to 1/16, then back up — produces a pixelated blur.
  const small = document.createElement("canvas");
  small.width = Math.max(1, Math.floor(SLIDE_W / 16));
  small.height = Math.max(1, Math.floor(SLIDE_H / 16));
  const sctx = small.getContext("2d");
  sctx.drawImage(img, 0, 0, small.width, small.height);
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(small, 0, 0, SLIDE_W, SLIDE_H);
}

function drawBrandBadge(ctx, carousel, badgeRgb, x, y, light = false) {
  const businessName = (carousel.business_name || "").toUpperCase();
  if (!businessName) return;
  ctx.font = `900 36px ${FAMILY}`;
  const text = `  ${businessName}  `;
  const w = ctx.measureText(text).width;
  const h = 56;
  ctx.fillStyle = light ? "rgba(255,255,255,0.18)" : rgbCss(badgeRgb);
  ctx.fillRect(x, y, w + 12, h);
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#fff";
  ctx.fillText(text, x + 6, y + h / 2 + 2);
  ctx.textBaseline = "alphabetic";
}

function drawSwipeHint(ctx, rgb) {
  ctx.font = `700 28px ${FAMILY}`;
  ctx.fillStyle = rgbCss(rgb, 0.85);
  const text = "SCORRI →";
  const w = ctx.measureText(text).width;
  ctx.fillText(text, SLIDE_W - w - 60, SLIDE_H - 50);
}

function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ---------- public API ----------

/**
 * Render one carousel slide to a PNG Blob.
 */
export async function renderSlideBlob(slide, carousel) {
  const canvas = document.createElement("canvas");
  canvas.width = SLIDE_W;
  canvas.height = SLIDE_H;
  const ctx = canvas.getContext("2d", { alpha: false });

  // "custom" layout mode overrides the legacy cover/body/outro routines.
  // Same drawCustomSlide is used for every slide role: the user has
  // explicitly opted into a manual layout, so we honor it on every slide.
  if (carousel?.layout_mode === "custom") {
    await drawCustomSlide(ctx, slide, carousel);
  } else if (slide.role === "cover") {
    await drawCoverSlide(ctx, slide, carousel);
  } else if (slide.role === "outro") {
    await drawOutroSlide(ctx, slide, carousel);
  } else {
    await drawBodySlide(ctx, slide, carousel);
  }

  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
      "image/png",
      // PNG is lossless; passes Instagram's quality requirements.
    );
  });
}

/**
 * Render all slides + zip them into a single archive Blob.
 */
export async function renderCarouselZip(carousel, { onProgress, backendUrl = "" } = {}) {
  const zip = new JSZip();
  const slides = carousel.slides || [];
  for (let i = 0; i < slides.length; i++) {
    if (slides[i].video_id) {
      // Video slide: fetch the original MP4 from the backend and include
      // it in the zip alongside the PNG slides. Instagram supports
      // mixed image+video carousels natively.
      try {
        const res = await fetch(`${backendUrl}/api/reels/uploads/video/${slides[i].video_id}`);
        if (res.ok) {
          const buf = await res.arrayBuffer();
          zip.file(`slide_${String(i + 1).padStart(2, "0")}.mp4`, buf);
        }
      } catch {
        // best-effort: skip video on fetch failure, the rest of the zip still works
      }
    } else {
      const blob = await renderSlideBlob(slides[i], carousel);
      zip.file(`slide_${String(i + 1).padStart(2, "0")}.png`, blob);
    }
    if (onProgress) onProgress(Math.round(((i + 1) / slides.length) * 95));
  }
  if (onProgress) onProgress(98);
  const out = await zip.generateAsync({ type: "blob" });
  if (onProgress) onProgress(100);
  return out;
}

/**
 * Render every slide and return them as an array suitable for
 * `navigator.share({files: [...]})`. Image slides produce a PNG File,
 * video slides produce the original MP4 File — Instagram accepts
 * mixed-media carousels via the system share sheet.
 */
export async function renderCarouselFiles(carousel, { onProgress, backendUrl = "" } = {}) {
  const slides = carousel.slides || [];
  const files = [];
  for (let i = 0; i < slides.length; i++) {
    const baseName = `slide_${String(i + 1).padStart(2, "0")}`;
    if (slides[i].video_id) {
      try {
        const res = await fetch(`${backendUrl}/api/reels/uploads/video/${slides[i].video_id}`);
        if (res.ok) {
          const blob = await res.blob();
          const mime = blob.type || "video/mp4";
          files.push(new File([blob], `${baseName}.mp4`, { type: mime }));
        }
      } catch {
        // best-effort: skip on failure
      }
    } else {
      const blob = await renderSlideBlob(slides[i], carousel);
      files.push(new File([blob], `${baseName}.png`, { type: "image/png" }));
    }
    if (onProgress) onProgress(Math.round(((i + 1) / slides.length) * 100));
  }
  return files;
}
